import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { writeKarlOpening } from "../writeKarlOpening";
import type { CablesChatMessage, KarlChatSource, UseCablesChat } from "./types";

// REACT_APP_AI_URL lets the chat hit a locally running backend while the rest
// of the app stays pointed at a deployed one.
const API_URL =
  process.env.REACT_APP_AI_URL ||
  (process.env.NODE_ENV === "production"
    ? ""
    : process.env.REACT_APP_BACKEND_URL || "http://localhost:5050");

// A reload used to lose the whole conversation. It lives per project+plan so
// switching plans is a different thread, not a continuation of the last one.
//
// v2 abandons every thread written before placements stopped being posted as
// the estimator's own words. Those threads open with a sentence nobody typed,
// and they are stored, so no fix upstream can reach them.
//
// Keyed by language too: every word in a thread is written by the run in the
// language that was on at the time, and replaying a German thread into an
// English panel put German prose and German follow-ups under English controls.
//
// And keyed by the RUN. Karl's opening sentence quotes the run's own figures —
// "6227.51 m across 83 circuits" — and it is prose, frozen the moment it was
// written. Re-running the takeoff left that sentence sitting above a panel
// reading 79 circuits and 5526.82 m, with nothing on screen to say which was
// current. A new run is a new conversation.
//
// v4 abandons every opening that quoted a total before the three steps were done.
const threadKey = (
  projectId: string,
  planName: string,
  lang: string,
  runStamp: string,
): string => `karl.cables.v4.${lang}.${runStamp}.${projectId}.${planName}`;

/** Undated runs all share one thread rather than each opening a new one. */
const runStampOf = (generatedAt: string | undefined): string =>
  generatedAt?.trim() || "undated";

/** The two the request itself distinguishes — anything else is English. */
const threadLang = (language: string | undefined): string =>
  language?.startsWith("de") ? "de" : "en";

const MAX_STORED_MESSAGES = 60;

interface StoredThread {
  messages: CablesChatMessage[];
  suggestions: string[];
}

const readThread = (key: string): StoredThread | null => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredThread;
    // Cards are re-offered from the walkthrough's own state, so a thread that
    // holds nothing but cards is not a conversation — treating it as one left
    // an orphaned card on screen and Karl silent, having never opened.
    const hasProse = parsed.messages?.some((entry) => entry.role !== "card");
    return hasProse ? parsed : null;
  } catch {
    return null;
  }
};

const writeThread = (key: string, thread: StoredThread): void => {
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        messages: thread.messages.slice(-MAX_STORED_MESSAGES),
        suggestions: thread.suggestions,
      }),
    );
  } catch {
    // A full or blocked store is not worth breaking the chat over.
  }
};

interface CablesChatReply {
  answer?: string;
  suggestions?: string[];
  focusCircuit?: string | null;
}

export const useCablesChat: UseCablesChat = ({
  projectId,
  planName,
  gaps,
  generatedAt,
  onFocusCircuit,
  isActive = true,
  liveTakeoff,
  isSetupDone,
  onFreshThread,
  openingFacts,
}) => {
  const { t, i18n } = useTranslation();
  const lang = threadLang(i18n.language);
  const runStamp = runStampOf(generatedAt);
  const [messages, setMessages] = useState<CablesChatMessage[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const openedFor = useRef<string | null>(null);
  // Read inside ask() so it does not have to depend on `messages`, which would
  // rebuild ask/send/reset on every turn and re-fire their consumers' effects.
  const inFlight = useRef(false);
  const messagesRef = useRef<CablesChatMessage[]>([]);
  messagesRef.current = messages;
  // Read at send time: it changes with every edit, and a dependency would rebuild ask on each one.
  const liveTakeoffRef = useRef(liveTakeoff);
  liveTakeoffRef.current = liveTakeoff;
  const isSetupDoneRef = useRef(isSetupDone);
  isSetupDoneRef.current = isSetupDone;
  const onFreshThreadRef = useRef(onFreshThread);
  onFreshThreadRef.current = onFreshThread;
  const openingFactsRef = useRef(openingFacts);
  openingFactsRef.current = openingFacts;
  // Given but still empty means the plan is being measured: greeting now would quote the agent's figures.
  const isLiveTakeoffReady = liveTakeoff === undefined || liveTakeoff.length > 0;
  // Null after the steps means the first calculation is still running; undefined means the caller gives no figures.
  const isOpeningReady = !isSetupDone || openingFacts !== null;

  const ask = useCallback(
    async (
      message: string,
      mode: "opening" | "chat" | "event",
      source: KarlChatSource,
      note?: string,
    ): Promise<void> => {
      if (!projectId || inFlight.current) return;
      // The opening says only what the app already knows, so it is written here: a model round trip kept the panel blank for seconds.
      if (mode === "opening") {
        const facts = openingFactsRef.current;
        if (!isSetupDoneRef.current || facts) {
          const content = writeKarlOpening({ facts: facts ?? null, t, language: lang });
          setMessages([{ role: "assistant", content }]);
          setSuggestions([]);
          return;
        }
      }
      inFlight.current = true;

      const history = mode === "opening" ? [] : messagesRef.current;
      if (mode === "chat") {
        setMessages([...history, { role: "user", content: message }]);
      }
      if (mode === "event") {
        setMessages([...history, { role: "event", content: note ?? message }]);
      }
      setSuggestions([]);
      setIsSending(true);
      try {
        const res = await fetch(`${API_URL}/api/ai/cables-chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            projectId,
            planName: planName ?? undefined,
            language: lang,
            mode: mode === "event" ? "chat" : mode,
            message,
            // Karl's side of the wire only knows two roles; an event is a turn
            // he has to answer, so it travels as one.
            conversationHistory: history.map((entry) => ({
              role: entry.role === "assistant" ? "assistant" : "user",
              content: entry.content,
            })),
            liveTakeoff: liveTakeoffRef.current || undefined,
            isSetupDone: isSetupDoneRef.current,
            gaps: gaps.map(({ kind, severity, summary, evidence, ask }) => ({
              kind,
              severity,
              summary,
              evidence,
              ask: ask.kind === "none" ? undefined : ask.prompt,
            })),
          }),
        });
        const data = (await res.json()) as CablesChatReply;
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_KARL_CHAT_ANSWER_RECEIVED, {
          source,
          suggestionCount: data.suggestions?.length ?? 0,
        });
        setMessages((current) => [
          ...current,
          {
            role: "assistant",
            content: data.answer ?? t("smartSurveyor.cablesBeta.chatNoAnswer"),
          },
        ]);
        setSuggestions(data.suggestions ?? []);
        if (data.focusCircuit) onFocusCircuit?.(data.focusCircuit);
      } catch {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_KARL_CHAT_ANSWER_FAILED, {
          source,
        });
        setMessages((current) => [
          ...current,
          {
            role: "assistant",
            content: t("smartSurveyor.cablesBeta.chatUnreachable"),
          },
        ]);
      } finally {
        inFlight.current = false;
        setIsSending(false);
      }
    },
    [projectId, planName, gaps, lang, t, onFocusCircuit],
  );

  const send = useCallback(
    async (message: string, source: KarlChatSource): Promise<void> => {
      const trimmed = message.trim();
      if (!trimmed) return;
      await ask(trimmed, "chat", source);
    },
    [ask],
  );

  // A wizard card is a message like any other, so it keeps its place in the
  // thread and survives a reload with the conversation around it.
  const addCard = useCallback((stepKey: string): void => {
    setMessages((current) =>
      current.some(
        (entry) => entry.role === "card" && entry.content === stepKey,
      )
        ? current
        : [...current, { role: "card", content: stepKey }],
    );
  }, []);

  const report = useCallback(
    async (note: string, question: string): Promise<void> => {
      await ask(question, "event", "boardMoved", note);
    },
    [ask],
  );

  const reset = useCallback(() => {
    setMessages([]);
    setSuggestions([]);
    if (projectId && planName) {
      try {
        window.localStorage.removeItem(
          threadKey(projectId, planName, lang, runStamp),
        );
      } catch {
        // ignore
      }
    }
    void ask("", "opening", "opening");
  }, [projectId, planName, lang, runStamp, ask]);

  // Restore the thread for this plan, or have Karl open one so the panel is
  // never an empty box waiting to be prompted.
  useEffect(() => {
    if (!isActive || !projectId || !planName) return;
    const key = threadKey(projectId, planName, lang, runStamp);
    if (openedFor.current === key) return;

    const saved = readThread(key);
    if (saved) {
      openedFor.current = key;
      setMessages(saved.messages);
      setSuggestions(saved.suggestions ?? []);
      return;
    }
    if (!isLiveTakeoffReady || !isOpeningReady) return;
    openedFor.current = key;
    setMessages([]);
    setSuggestions([]);
    onFreshThreadRef.current?.();
    void ask("", "opening", "opening");
    // `ask` changes with every message; this must run only on plan change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, projectId, planName, lang, runStamp, isLiveTakeoffReady, isOpeningReady]);

  useEffect(() => {
    if (!projectId || !planName || !messages.length) return;
    writeThread(threadKey(projectId, planName, lang, runStamp), {
      messages,
      suggestions,
    });
  }, [projectId, planName, lang, runStamp, messages, suggestions]);

  return { messages, suggestions, isSending, send, report, addCard, reset };
};
